import { EventEmitter } from '@angular/core';
import { CollectionViewer, DataSource } from '@angular/cdk/collections';

import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';

import { Observable, ReplaySubject, merge } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import { BaseListReqParams } from 'modules/shared/models/base.models';

export interface IDataSourceBaseSettings {
  sort?: MatSort;
  paginator?: MatPaginator;
  paginatorConfigs?: {
    pageSizeOptions: Array<number>;
  };
}

export abstract class BaseDataSource<T> implements DataSource<T> {

  protected unsubscribe$: ReplaySubject<any> = new ReplaySubject<any>(1);

  // Request payload
  protected _payload: BaseListReqParams = {};
  // Observable with data used in connect method for populating
  protected _dataObservable$: Observable<Array<T>>;

  // Data is loading
  isLoading$: Observable<boolean>;

  // Was data tried to load once or not
  isFirstTimeLoaded: boolean = false;

  // Array with all data displayed on the current page
  currentPage: Array<T> = [];

  sort: MatSort;
  paginator: MatPaginator;

  constructor() { }

  get payload(): BaseListReqParams {
    return this._payload;
  }

  get dataObservable$(): Observable<Array<T>> {
    return this._dataObservable$;
  }

  /**
   * Default data source method
   * @method connect
   */
  connect(collectionViewer: CollectionViewer): Observable<Array<T>> {
    const tableEvents$: Array<EventEmitter<Sort | PageEvent>> = [];

    if (this.sort) {
      tableEvents$.push(this.sort.sortChange);
    }

    if (this.paginator) {
      tableEvents$.push(this.paginator.page);
    }

    merge(...tableEvents$)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.updateTablePayload();
        this.loadData();
      });

    // Takes data and sets to internal variable
    // Set flag for init, do not forget to skip first store value
    return this.dataObservable$
      .pipe(tap((data: Array<T>) => {
        this.currentPage = data;
        this.isFirstTimeLoaded = true;
      }));
  }

  /**
   * Default data source method
   * @method disconnect
   */
  disconnect(collectionViewer: CollectionViewer): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  /**
   * Integrates data source with all features like pagi, sort and etc.
   * @method initDataSource
   */
  initDataSource(settings: IDataSourceBaseSettings): void {
    if (settings.sort) {
      this.sort = settings.sort;
    }

    if (settings.paginator) {
      this.paginator = settings.paginator;

      Object.entries(settings.paginatorConfigs).forEach(([key, value]: [string, any]) => {
        this.paginator[key] = value;
      });
    }

    this.updateTablePayload();
  }

  /**
   * Sets pagination or sorting data to payload
   * @method updateTablePayload
   */
  updateTablePayload(): void {
    if (this.sort) {
      switch (this.sort.direction) {
        case 'asc':
          this.payload.sort = `${this.sort.active}`;
          break;

        case 'desc':
          this.payload.sort = `-${this.sort.active}`;
          break;

        default:
          this.payload.sort = undefined;
          break;
      }
    }

    if (this.paginator) {
      if (!this.paginator.pageSize) {
        this.paginator.pageSize = this.paginator.pageSizeOptions[0];
      }

      this.payload.limit = this.paginator.pageSize;
      this.payload.offset = this.paginator.pageSize * this.paginator.pageIndex;
    }
  }

  /**
   * Sets payload and update UI to new payload
   * @method setPayload
   */
  setPayload(payload?: BaseListReqParams): void {
    // Cannot update sorting UI, material doesn't support
    // if (payload.sort !== undefined) {
    //   const sortingArray: Array<string> = payload.sort.split('-');
    //   const sortingObject: any = {
    //     id: sortingArray.length === 1 ? sortingArray[0] : sortingArray[1],
    //     start: sortingArray.length === 1 ? 'asc' : 'desc'
    //   }
    // }

    if (this.paginator && (payload.limit !== undefined || payload.offset !== undefined)) {
      const newLimit: number = payload.limit ? payload.limit : this.paginator.pageSize;
      const newOffset: number = payload.offset ? payload.offset : this.paginator.pageSize * this.paginator.pageIndex;

      this.paginator.pageSize = newLimit;
      this.paginator.pageIndex = newOffset / newLimit;
      this.paginator.length = this.paginator.length;
    }

    this._payload = {
      ...this.payload,
      ...payload
    };
  }

  /**
   * Sets observable which returns data, used in data source connect method
   * @method setDataObservable
   */
  protected abstract setDataObservable(): void;

  /**
   * Abstract method which triggers loading data to get it in data observable
   * @method loadData
   */
  abstract loadData(): void;

}
